"use client";

import { c as _c } from "react/compiler-runtime";
import { useLocale, useTranslations } from "next-intl";
import { useRouter, usePathname } from "@/i18n/navigation";
import { locales, localeLabels } from "@/i18n/routing";
import type { Locale } from "@/i18n/routing";
import { ConfigProvider, Select } from "antd";
import React, { useTransition } from "react";
import styles from "./Header.module.sass";

// The switcher sits on the translucent, blurred header, so it must not paint its
// own opaque background, and its focus ring should match the header's dark accent
// rather than antd's default blue. antd v6 exposes both as component tokens; the
// old CSS-module overrides targeted the antd v5 `.ant-select-selector` wrapper and
// had been dead since the v6 migration. Border colours live in `Header.module.sass`
// because they are shared with the hover state and need `!important` to win.
const selectTheme = {
  components: {
    Select: {
      selectorBg: "transparent",
      activeOutlineColor: "rgba(51, 51, 51, 0.12)"
    }
  }
} as const;
export const LanguageSwitcher: React.FunctionComponent = () => {
  const $ = _c(11);
  const t = useTranslations("header");
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const [isPending, startTransition] = useTransition();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = locales.map(_temp);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const options = t0;
  const t1 = locale as Locale;
  let t2;
  if ($[1] !== t) {
    t2 = t("selectLanguage");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== pathname || $[4] !== router) {
    t3 = nextLocale => {
      startTransition(() => {
        router.replace(pathname, {
          locale: nextLocale
        });
      });
    };
    $[3] = pathname;
    $[4] = router;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== isPending || $[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = <ConfigProvider theme={selectTheme} data-sentry-element="ConfigProvider" data-sentry-component="LanguageSwitcher" data-sentry-source-file="LanguageSwitcher.tsx"><Select className={styles.language_switcher} value={t1} options={options} disabled={isPending} aria-label={t2} popupMatchSelectWidth={false} onChange={t3} data-sentry-element="Select" data-sentry-source-file="LanguageSwitcher.tsx" /></ConfigProvider>;
    $[6] = isPending;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
function _temp(loc) {
  return {
    value: loc,
    label: localeLabels[loc]
  };
}